// RogueSamplingSummary — the panel that replaces the live-feed slot
// in LiveCore during the `results_pending_rogue` phase.
//
// What it does: surfaces the EXISTING sampling pipeline's output —
// pooled per-party clean reads, abstain rate, districts covered, and
// the Wilson-95 certainty ladder (INSUFFICIENT → DIRECTIONAL →
// PROJECTED) — transparently, alongside the live counting-hall
// stream that's still in the left LiveCore slot. Stage A is a
// DISPLAY layer over the existing pipeline; no capture-site or wire-
// format changes (that's Scope B).
//
// CRITICAL — the panel never calls the result. The hero winner
// announcement remains the existing /admin/announce editorial action
// (winner > turnout > default hero precedence). Even at PROJECTED
// the panel says "our sample indicates X" — not "X has won". A human
// flips nationalCall via the admin form and that propagates the
// hero call across results_pending(_rogue) and counting. Nothing in
// this file writes nationalCall or any other editorial field.
//
// Data path: reuses useElectionDaySampling() (public GET on
// /api/electionday-state?kind=sampling, edge-cached) and runs the
// same computeStats() the existing public /electionday StatsPanel
// runs. A test-data override prop is supported so the admin preview
// surface (Commit 3) can drive the panel with synthetic batches
// WITHOUT touching the real sampling store.

import { useMemo } from 'react';
import { useElectionDaySampling } from '../../lib/useElectionDaySampling';
import { useRogueTestData } from '../../lib/useRogueTestData';
import {
  computeStats,
  MIN_CLEAN_DIRECTIONAL,
  MIN_CLEAN_PROJECTED,
  MIN_DISTRICTS_COVERED,
  PROJECTED_MIN_GAP,
  TOTAL_DISTRICTS,
  type Batch,
  type NationalStats,
} from '../ElectionDay/stats';
import './RogueSamplingSummary.css';

interface Props {
  /** Optional explicit override: when provided, the panel computes
   *  its stats from THIS array of batches instead of either the
   *  public sampling state OR the operator's localStorage test
   *  store. Used by the admin preview surface's dry-run carousel.
   *  Most callers won't pass this — the panel falls back to the
   *  useRogueTestData hook (active iff the operator has loaded a
   *  preset) and then to the live sampling state. */
  testBatches?: Batch[] | null;
  /** Loud "PREVIEW / TEST DATA" banner. Defaults to true when an
   *  explicit testBatches prop is set, OR when the local test-data
   *  store is active. */
  testMode?: boolean;
}

export default function RogueSamplingSummary({
  testBatches = null,
  testMode,
}: Props) {
  // Always call both hooks (rules of hooks). The active test-data
  // store wins over live sampling when the operator has loaded a
  // preset; passing testBatches explicitly wins over both for the
  // admin preview surface's local dry-runs.
  const { data: sampling } = useElectionDaySampling();
  const { batches: storedTestBatches, isActive: storeIsActive } =
    useRogueTestData();

  const batches: Batch[] =
    testBatches !== null
      ? testBatches
      : storeIsActive
        ? storedTestBatches
        : sampling.committedBatches;

  // Effective test-mode banner: explicit prop wins; otherwise true
  // whenever the local test-data store is driving the panel.
  const showTestBanner =
    testMode !== undefined ? testMode : testBatches !== null || storeIsActive;

  const stats = useMemo<NationalStats>(
    () => computeStats(batches),
    [batches],
  );

  const noData = batches.length === 0 || stats.clean === 0;

  return (
    <section
      className={`rogue-summary${showTestBanner ? ' is-test' : ''}`}
      aria-label="Lovin Malta live sample"
    >
      {showTestBanner && (
        <div className="rogue-summary__test-banner" role="alert">
          ⚠ Preview / test data — not connected to the live sampling store.
        </div>
      )}

      <header className="rogue-summary__head">
        <div>
          <p className="rogue-summary__kicker">Lovin Malta live sample</p>
          <h2 className="rogue-summary__title">
            {noData
              ? 'Awaiting first sample'
              : stats.callState === 'PROJECTED'
                ? stats.leader
                  ? `Our sample now indicates ${stats.leader} — 95% confidence`
                  : 'Our sample reached projection threshold'
                : stats.callState === 'DIRECTIONAL'
                  ? stats.leader
                    ? `Directional: ${stats.leader} ahead, not yet conclusive`
                    : 'Directional, not yet conclusive'
                  : 'Building the sample — early reads only'}
          </h2>
        </div>
        <CallBadge state={stats.callState} leader={stats.leader} />
      </header>

      {noData ? (
        <AwaitingPanel />
      ) : (
        <>
          <TallyRow stats={stats} />
          <CertaintyBlock stats={stats} />
          <CoverageRow stats={stats} />
        </>
      )}

      <footer className="rogue-summary__methodology">
        <strong>How this works.</strong>{' '}
        Lovin Malta agents film the votes shown to party representatives
        as ballots are sorted. Each frame is classified for which party
        the #1 preference went to; clean reads pool into a running
        national sample. The certainty figure is a Wilson 95% interval
        on the PL/PN share — projection requires{' '}
        <strong>≥ {MIN_CLEAN_PROJECTED.toLocaleString()} clean reads</strong>{' '}
        across <strong>≥ {MIN_DISTRICTS_COVERED} districts</strong>{' '}
        with a margin of at least{' '}
        <strong>{(PROJECTED_MIN_GAP * 100).toFixed(1)} percentage points</strong>.
        The official call still comes from the Electoral Commission;
        this is our independent statistical reading. Anything published
        as a Lovin Malta "call" comes from a separate editorial action
        — this panel only displays what the maths says.
      </footer>
    </section>
  );
}

// ── Sub-components ─────────────────────────────────────────────────

function AwaitingPanel() {
  return (
    <div className="rogue-summary__awaiting">
      <p>
        No committed sampling batches yet. The panel will populate as
        the first videos come in and the team commits batches from{' '}
        <code>/admin/electionday</code>.
      </p>
      <p className="rogue-summary__awaiting-note">
        Need{' '}
        <strong>{MIN_CLEAN_DIRECTIONAL.toLocaleString()} clean reads</strong>{' '}
        across{' '}
        <strong>{MIN_DISTRICTS_COVERED} districts</strong> before a
        directional read; {MIN_CLEAN_PROJECTED.toLocaleString()} clean
        reads + a ≥ {(PROJECTED_MIN_GAP * 100).toFixed(1)}pp margin
        before projection.
      </p>
    </div>
  );
}

function CallBadge({
  state,
  leader,
}: {
  state: NationalStats['callState'];
  leader: NationalStats['leader'];
}) {
  const label =
    state === 'PROJECTED'
      ? leader
        ? `Projected · ${leader}`
        : 'Projected'
      : state === 'DIRECTIONAL'
        ? leader
          ? `Directional · ${leader} ahead`
          : 'Directional'
        : 'Insufficient';
  return (
    <span className={`rogue-summary__badge rogue-summary__badge--${state.toLowerCase()}`}>
      {label}
    </span>
  );
}

function TallyRow({ stats }: { stats: NationalStats }) {
  const callIsLive = stats.callState !== 'INSUFFICIENT';
  const plShare = callIsLive ? (stats.plShare * 100).toFixed(1) : null;
  const pnShare = callIsLive ? ((1 - stats.plShare) * 100).toFixed(1) : null;
  return (
    <div className="rogue-summary__tiles">
      <PartyTile
        party="PL"
        clean={stats.pl}
        sharePct={plShare}
        ci={
          callIsLive
            ? `${(stats.plInterval.lo * 100).toFixed(1)}–${(stats.plInterval.hi * 100).toFixed(1)}%`
            : null
        }
      />
      <PartyTile
        party="PN"
        clean={stats.pn}
        sharePct={pnShare}
        ci={
          callIsLive
            ? `${(stats.pnInterval.lo * 100).toFixed(1)}–${(stats.pnInterval.hi * 100).toFixed(1)}%`
            : null
        }
      />
    </div>
  );
}

function PartyTile({
  party,
  clean,
  sharePct,
  ci,
}: {
  party: 'PL' | 'PN';
  clean: number;
  sharePct: string | null;
  ci: string | null;
}) {
  return (
    <div className={`rogue-summary__tile rogue-summary__tile--${party.toLowerCase()}`}>
      <span className="rogue-summary__tile-label">{party}</span>
      <span className="rogue-summary__tile-big">
        {sharePct !== null ? `${sharePct}%` : clean.toLocaleString()}
      </span>
      <span className="rogue-summary__tile-sub">
        {sharePct !== null
          ? `${clean.toLocaleString()} clean reads`
          : `clean reads · share waits for ≥ ${MIN_CLEAN_DIRECTIONAL} pooled`}
      </span>
      {ci && (
        <span className="rogue-summary__tile-ci">95% CI {ci}</span>
      )}
    </div>
  );
}

function CertaintyBlock({ stats }: { stats: NationalStats }) {
  const gapPp = Math.abs(stats.plShare - 0.5) * 200; // (share-0.5)*2 = lead, ×100 for pp
  if (stats.callState === 'PROJECTED') {
    return (
      <div className="rogue-summary__certainty rogue-summary__certainty--projected">
        Projection threshold met:{' '}
        <strong>{stats.clean.toLocaleString()}</strong> clean reads across{' '}
        <strong>{stats.districtsCovered}</strong> districts,{' '}
        <strong>{gapPp.toFixed(1)} pp</strong> lead.
        {' '}The Wilson 95% interval no longer crosses 50/50 — the sample
        is statistically robust to a projected outcome. A human still
        announces; the official call is the commission's.
      </div>
    );
  }
  if (stats.callState === 'DIRECTIONAL') {
    return (
      <div className="rogue-summary__certainty rogue-summary__certainty--directional">
        Directional read:{' '}
        <strong>{stats.clean.toLocaleString()}</strong> clean reads.
        The 95% interval sits on one side of 50/50 but the sample
        isn't yet tight enough to project. Need{' '}
        <strong>
          {stats.cleanReadsToProjected.toLocaleString()} more clean
          reads
        </strong>
        {stats.stillsToProjected !== null && (
          <>
            {' '}(≈ {stats.stillsToProjected.toLocaleString()} stills at the
            current {Math.round(stats.acceptRate * 100)}% accept rate)
          </>
        )}
        {gapPp < PROJECTED_MIN_GAP * 100 && (
          <>
            {' '}and a lead of ≥ {(PROJECTED_MIN_GAP * 100).toFixed(1)} pp
            (currently {gapPp.toFixed(1)})
          </>
        )}
        .
      </div>
    );
  }
  // INSUFFICIENT — explain what's missing, honestly.
  const reasons: string[] = [];
  if (stats.clean < MIN_CLEAN_DIRECTIONAL) {
    reasons.push(
      `${(MIN_CLEAN_DIRECTIONAL - stats.clean).toLocaleString()} more clean reads`,
    );
  }
  if (stats.districtsCovered < MIN_DISTRICTS_COVERED) {
    reasons.push(
      `${MIN_DISTRICTS_COVERED - stats.districtsCovered} more district${MIN_DISTRICTS_COVERED - stats.districtsCovered === 1 ? '' : 's'} covered`,
    );
  }
  return (
    <div className="rogue-summary__certainty rogue-summary__certainty--insufficient">
      Building the sample.{' '}
      <strong>{stats.clean.toLocaleString()}</strong> clean reads so far
      across <strong>{stats.districtsCovered}</strong> districts.
      {reasons.length > 0 && (
        <>
          {' '}Need {reasons.join(' and ')} before a directional read.
        </>
      )}
      {' '}Anything we show here is too early to indicate an outcome.
    </div>
  );
}

function CoverageRow({ stats }: { stats: NationalStats }) {
  return (
    <div className="rogue-summary__coverage">
      <CoverageStat
        label="Clean reads"
        value={stats.clean.toLocaleString()}
      />
      <CoverageStat
        label="Abstained"
        value={`${stats.abstained.toLocaleString()} (${(stats.abstainRate * 100).toFixed(0)}%)`}
      />
      <CoverageStat
        label="Districts"
        value={`${stats.districtsCovered} / ${TOTAL_DISTRICTS}`}
      />
      <CoverageStat
        label="Stills processed"
        value={stats.totalStills.toLocaleString()}
      />
    </div>
  );
}

function CoverageStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rogue-summary__coverage-stat">
      <span className="rogue-summary__coverage-label">{label}</span>
      <span className="rogue-summary__coverage-value">{value}</span>
    </div>
  );
}
